'use client'

import { createContext, useContext, useEffect, useRef, useState } from 'react'

import { IS_PRODUCTION } from '@/shared/lib'

import { LOCK_AB_OVERRIDE_PARAM, LOCK_AB_VARIANT } from '../_constants'
import {
  buildLockAbCookie,
  LOCK_AB_EXCLUDED,
  readLockAbCookie,
  resolveRankLockAb,
} from '../_utils'

import type { ILockAbAssignment } from '../_utils'
import type { ReactNode } from 'react'

const LockAbContext = createContext<ILockAbAssignment>(LOCK_AB_EXCLUDED)

const useWriteLockAbCookie = (assignment: ILockAbAssignment) => {
  const writtenRef = useRef(false)
  useEffect(
    function writeLockAbCookieOnce() {
      if (writtenRef.current || !assignment.writeCookie) return
      writtenRef.current = true
      document.cookie = buildLockAbCookie(assignment.variant)
    },
    [assignment]
  )
}

interface ILockAbProviderProps {
  assignment: ILockAbAssignment
  children: ReactNode
}

/** 서버 배정 결과를 하위에 넘기고 쿠키 만료를 갱신한다 */
export const LockAbProvider = ({
  assignment,
  children,
}: ILockAbProviderProps) => {
  useWriteLockAbCookie(assignment)
  return (
    <LockAbContext.Provider value={assignment}>
      {children}
    </LockAbContext.Provider>
  )
}

export const useLockAb = () => {
  const assignment = useContext(LockAbContext)
  return {
    data: {
      ...assignment,
      isLock: assignment.variant === LOCK_AB_VARIANT.LOCK,
    },
  }
}

/** 진입당 1회 보내고, from이 null(인앱·크롤러·쿠키 없음)이면 보내지 않는다. 전송 함수는 페이지별 트래커를 받는다 */
export const useLockAbExposure = (
  assignment: ILockAbAssignment,
  send: (assignment: ILockAbAssignment) => void
) => {
  const sentRef = useRef(false)
  const sendRef = useRef(send)
  sendRef.current = send

  useEffect(
    function sendLockAbExposureOnce() {
      if (sentRef.current || !assignment.from) return
      sentRef.current = true
      sendRef.current(assignment)
    },
    [assignment]
  )
}

/**
 * 랭킹은 새로 배정하지 않고 마운트 후 쿠키로 판정한다. SSR·첫 렌더는 실험 제외 값이고, 쿠키가 있으면 만료를 연장한다.
 * useIsInApp은 첫 렌더에서 false라 그 값으로 판정하면 인앱에서도 노출 이벤트가 나가서, 판정 함수로 받는다.
 */
export const useRankLockAb = (
  checkIsInApp: () => boolean
): ILockAbAssignment => {
  const [assignment, setAssignment] =
    useState<ILockAbAssignment>(LOCK_AB_EXCLUDED)
  const checkIsInAppRef = useRef(checkIsInApp)
  checkIsInAppRef.current = checkIsInApp

  useEffect(function resolveRankLockAbOnMount() {
    const overrideValue = IS_PRODUCTION
      ? null
      : new URLSearchParams(window.location.search).get(LOCK_AB_OVERRIDE_PARAM)
    setAssignment(
      resolveRankLockAb({
        cookieValue: readLockAbCookie(document.cookie),
        overrideValue,
        isInApp: checkIsInAppRef.current(),
        userAgent: navigator.userAgent,
      })
    )
  }, [])

  useWriteLockAbCookie(assignment)

  return assignment
}
